<template>
  <div class="artwork-feedback">
    <template
      v-if="
        showThankYou || (artwork.enhancementFeedbackSubmitted && artwork.enhancementRatingSubmitted)
      "
    >
      <div class="artwork-feedback__thankyou">
        <UseSvg icon="check-circle-primary" width="24" height="24" />
        <span>Thank you for your feedback!</span>
      </div>
    </template>
    <template v-else>
      <div class="artwork-feedback__header">
        <div class="artwork-feedback__question">
          Are you satisfied with how your image turned out? Let us know!
        </div>
        <div class="artwork-feedback__actions">
          <button
            type="button"
            class="artwork-feedback__thumb artwork-feedback__thumb-positive"
            aria-label="Satisfied"
            :disabled="artwork.enhancementRatingSubmitted"
            @click.prevent="handleThumbsUp"
          >
            <span class="artwork-feedback__thumb-positive-icon-container">
              <UseSvg icon="thumbs-up-green" width="24" height="24" />
            </span>
          </button>
          <button
            type="button"
            :class="[
              'artwork-feedback__thumb artwork-feedback__thumb-negative',
              { 'artwork-feedback__thumb-negative--active': satisfied === false }
            ]"
            aria-label="Not satisfied"
            @click.prevent="handleThumbsDown"
          >
            <span class="artwork-feedback__thumb-negative-icon-container">
              <UseSvg
                icon="thumbs-down"
                width="24"
                height="24"
                class="artwork-feedback__thumb-negative-icon"
              />
            </span>
          </button>
        </div>
      </div>
      <div v-if="satisfied === false" class="artwork-feedback__textarea-control">
        <div class="artwork-feedback__textarea-container">
          <label for="feedback-text" class="artwork-feedback__textarea-label">
            We're sorry the result didn't meet your expectations.
          </label>
          <span class="artwork-feedback__textarea-label-sub">
            Please let us know what didn't work for you.
          </span>
          <textarea
            id="feedback-text"
            v-model="feedback"
            placeholder="E.g. 'Still blurry', 'Colors are too strong', 'Lines got distorted'"
            rows="3"
            maxlength="300"
            class="artwork-feedback__textarea-input"
          />
          <div class="artwork-feedback__buttons">
            <button type="button" class="artwork-feedback__cancel" @click="reset">Cancel</button>
            <button
              type="button"
              :class="[
                'artwork-feedback__submit',
                { disabled: submitting || !feedback.trim() || feedback.length > 300 }
              ]"
              :disabled="submitting || !feedback.trim() || feedback.length > 300"
              @click="handleFeedbackSubmit"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { track } from "jiffy-analytics";
import { useDtfStore } from "components/jiffy/vue-product-details-dtf/store";
import UseSvg from "../vue-cart/UseSvg.vue";

const store = useDtfStore();
const props = defineProps({
  artwork: {
    type: Object,
    required: true
  },
  pageType: {
    type: String,
    required: true
  }
});

const satisfied = ref(props.artwork.enhancementSatisfied);
const feedback = ref("");
const submitting = ref(false);
const showThankYou = ref(false);

const reset = () => {
  feedback.value = "";
  submitting.value = false;
  showThankYou.value = false;
};

const handleThumbsUp = () => {
  markFeedbackSubmitted(true, true);
  triggerTracking("dtf_satisfaction_positive_clicked", {
    artworkId: props.artwork.id,
    pageType: props.pageType
  });
};

const handleFeedbackSubmit = () => {
  submitting.value = true;
  markFeedbackSubmitted(true, false);
  triggerTracking("dtf_satisfaction_feedback", {
    artworkId: props.artwork.id,
    message: feedback.value,
    pageType: props.pageType
  });
  reset();
  submitting.value = false;
};

const handleThumbsDown = () => {
  if (props.artwork.enhancementRatingSubmitted) {
    return;
  }

  markFeedbackSubmitted(false, false);
  triggerTracking("dtf_satisfaction_negative_clicked", {
    artworkId: props.artwork.id,
    pageType: props.pageType
  });
};

const triggerTracking = async (event, payload) => {
  track(event, payload);
};

const markFeedbackSubmitted = (feedbackSubmitted, isSatisfied) => {
  store.updateArtworkValuesById({
    id: props.artwork.id,
    enhancementFeedbackSubmitted: feedbackSubmitted,
    enhancementRatingSubmitted: true,
    enhancementSatisfied: isSatisfied
  });
  satisfied.value = isSatisfied;
  showThankYou.value = !!(isSatisfied || feedbackSubmitted);
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings";

.artwork-feedback {
  $this: &;

  border: 1px solid $primary-light;
  border-radius: 8px;
  position: relative;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  flex-direction: column;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  &__question {
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    flex: 1;
  }

  &__actions {
    display: flex;
    gap: 8px;
  }

  &__thumb {
    font-size: 2rem;
    border: none;
    cursor: pointer;
    opacity: 1;
    transition: all 0.2s;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;

    &:hover {
      transform: scale(1.05);
    }
  }

  &__thumb-positive {
    background: $crafterpay-gray-modern-200;
    fill: $crafterpay-gray-modern-200;
  }

  &__thumb-positive-icon-container {
    position: relative;
    bottom: 2px;
  }

  &__thumb-negative {
    background: $crafterpay-gray-modern-200;

    &--active {
      background: $thumbs-down-red;

      #{$this}__thumb-negative-icon {
        background: $thumbs-down-red;
        fill: $thumbs-down-red;
        color: $white;
      }
    }
  }

  &__thumb-negative-icon-container {
    position: relative;
    bottom: 1px;
  }

  &__thumb-negative-icon {
    background: $crafterpay-gray-modern-200;
    fill: $crafterpay-gray-modern-200;
    color: $thumbs-down-red;
  }

  &__textarea-control {
    width: 100%;
    margin-bottom: 12px;
  }

  &__textarea-container {
    width: 100%;
  }

  &__textarea-label {
    font-weight: 700;
    font-size: 15px;
    line-height: 18px;
    display: block;
    margin-bottom: 8px;
  }

  &__textarea-label-sub {
    font-weight: 400;
    font-size: 13px;
    line-height: 16px;
    color: $dark-gray;
    display: block;
    margin-bottom: 8px;
  }

  &__textarea-input {
    width: 100%;
    border-radius: 4px;
    border: 1px solid $light-tint-gray;
    padding: 8px;
    font-size: 1rem;
    resize: vertical;
    margin-bottom: 12px;
    box-sizing: border-box;
  }

  &__buttons {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  &__cancel {
    padding: 6px 16px;
    text-decoration: none;
    color: $primary-light;
    font-weight: 700;
    font-size: 14px;
  }

  &__submit {
    background: $primary-light;
    font-weight: 700;
    text-decoration: none;
    color: $white;
    border: none;
    border-radius: 4px;
    padding: 6px 16px;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  &__submit:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &__submit:hover:not(:disabled) {
    background: $primary-light;
    font-weight: 700;
    text-decoration: none;
  }

  &__thankyou {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-weight: 600;
    color: $dark-smoke-gray;
    padding: 12px 0;
    width: 100%;
    font-size: 16px;
    line-height: 24px;
    font-family: Inter, Arial, Helvetica, sans-serif;
  }
}
</style>
